import React, { useEffect, useMemo, useRef, useState } from 'react';

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useParams } from 'react-router-dom';
import './styleSheets/dataExporter.css';
import {
  fetchSiteData as fetchSiteDataFB,
  fetchFaultData as fetchFaultDataFB,
  fetchLatestDate,
  fetchPanelsForRepairDate,
  fetchOverlayData,
} from '../utils/firebase';

import {
  MapSection,
  MapEditPanel,
  MAP_DEFAULT_PROPS,
  useMapData,
  useBlockFeatures,
} from './reportBuilderTiles/mapTile';

import {
  REPAIR_DEFAULT_PROPS,
  RepairListSection,
  RepairListEditPanel,
  hydrateRepairList,
} from "./reportBuilderTiles/repairListTile";

import {
  fetchSiteData as fetchSiteDataDL,
  fetchFaultData as fetchFaultDataDL
} from '../utils/dataLoader';

import IconButton from '@mui/material/IconButton';
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import {
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Check as CheckIcon,
  Save as SaveIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";

const PVWATTS_API_URL = 'https://developer.nrel.gov/api/pvwatts/v6.json';
const PVWATTS_API_KEY = process.env.REACT_APP_PVWATTS_API_KEY;


// Section catalog
const SECTION_CATALOG = [
  { key: 'TITLE', label: 'Title' },
  { key: 'DATE', label: 'Date' },
  { key: 'OVERVIEW', label: 'Overview Statistics' },
  { key: 'ORTHO', label: 'Site Orthomosaic Images' },
  { key: 'FAULT_CHART', label: 'Fault Table' },
  { key: 'MAP', label: 'Map' },
  { key: 'REPAIR_LIST', label: 'Repair List' },
  { key: 'CUSTOM_TEXT', label: 'Text Box' },
];

const BREAK_POLICY = {
  TITLE: 'keep',
  DATE: 'keep',
  OVERVIEW: 'keep',
  MAP: 'keep',
  CUSTOM_TEXT: 'keep',
  ORTHO: 'keep',
  FAULT_CHART: 'split',
  REPAIR_LIST: 'split',
};

const makeInst = (kind) => ({ id: uid(), kind });

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Presets
const PRESETS = [
  //Site Overview template
  {
    key: 'SiteOverview',
    label: 'Site Overview',
    initial: (siteName, date) => {
      const title = makeInst('TITLE');
      const when = makeInst('DATE');
      const kpis = makeInst('OVERVIEW');
      const map = makeInst('MAP');
      const mapTitle = makeInst('TITLE')

      return {
        name: 'Site Overview',
        order: [title, when, kpis, mapTitle, map],
        sectionPropsById: {
          [title.id]: {
            text: 'Site Status Snapshot',
            size: 40,
            align: 'left',
          },
          [when.id]: {
            style: 'long',
            order: 'MDY',
            size: 14,
            align: 'left',
            margin: 6,
            showPrefix: true
          },
          [kpis.id]: {
            layout: 'row',
            size: 'lg',
            align: 'center',
            margin: 10,

            showTotal: true,
            showAffected: true,
            showPercent: true,
            showKwh: true,

            labelTotal: 'Total Panels',
            labelAffected: 'Affected',
            labelPercent: '% Affected',
            labelKwh: 'Est. kWh Lost',

            percentDecimals: 1,
            kwhDecimals: 0,
          },
          [mapTitle.id]: {
            text: 'Full Site Map',
            size: 26,
            align: 'left',
          },
          [map.id]: {
            marginTop: 8,
            marginBottom: 12,
            size: 'medium',
            showFaults: true,
          },
        },

        meta: {
          autoExpandBlocks: true,
          blockslimit: 999,
          onlyBlocksWithFaults: false
        },

        sectionProps: { COVER: {} },
        notes: '',
        siteName,
        selectedDate: date,
      };
    },
  },

  //technician template
  {
    key: 'technician',
    label: 'Site Repair',
    initial: (siteName, date) => {
      const title = makeInst('TITLE');
      const when = makeInst('DATE');
      const map = makeInst('MAP');
      const rlAll = makeInst('REPAIR_LIST');

      return {
        name: 'Site Repair',
        order: [title, when, map, rlAll],
        sectionPropsById: {
          [rlAll.id]: {},
        },
        sectionProps: { COVER: {} },
        notes: '',
        siteName,
        selectedDate: date,
      };
    },
  },

  //Statistics template
  {
    key: 'executive',
    label: 'Statistics Summary',
    initial: (siteName, date) => {
      const title = makeInst('TITLE');
      const when = makeInst('DATE');
      const kpis = makeInst('OVERVIEW');

      const atGlance = makeInst('TITLE');
      const siteMap = makeInst('MAP');

      const l3Title = makeInst('TITLE');
      const l3Table = makeInst('FAULT_CHART');

      const l2Title = makeInst('TITLE');
      const l2Table = makeInst('FAULT_CHART');

      const repairsTitle = makeInst('TITLE');
      const repairList = makeInst('REPAIR_LIST');

      return {
        name: 'Statistics Summary',
        order: [
          title, when, kpis,
          atGlance, siteMap,
          l3Title, l3Table,
          l2Title, l2Table,
          repairsTitle, repairList,
        ],
        sectionPropsById: {
          [title.id]: {
            text: 'Statistics Summary',
            size: 40,
            align: 'left',
          },
          [when.id]: {
            style: 'long',
            order: 'MDY',
            size: 14,
            align: 'left',
            margin: 6,
            showPrefix: true,
          },
          [kpis.id]: {
            layout: 'row',
            size: 'lg',
            align: 'center',
            margin: 10,
            showTotal: true,
            showAffected: true,
            showPercent: true,
            showKwh: true,
            labelTotal: 'Total Panels',
            labelAffected: 'Affected',
            labelPercent: '% Affected',
            labelKwh: 'Est. kWh Lost',
            percentDecimals: 1,
            kwhDecimals: 0,
          },

          [atGlance.id]: {
            text: 'At a Glance',
            size: 26,
            align: 'left',
          },
          [siteMap.id]: {
            size: 'medium',
            marginTop: 8,
            marginBottom: 12,
            showFaults: true,
          },

          [l3Title.id]: {
            text: 'Highest Severity (Level 3)',
            size: 22,
            align: 'left',
          },
          [l3Table.id]: {
            levels: { 'Level 1': false, 'Level 2': false, 'Level 3': true },
            exportSlice: { start: 0, end: 10 },
          },

          [l2Title.id]: {
            text: 'Moderate Severity (Level 2)',
            size: 22,
            align: 'left',
          },
          [l2Table.id]: {
            levels: { 'Level 1': false, 'Level 2': true, 'Level 3': false },
            exportSlice: { start: 0, end: 10 },
          },

          [repairsTitle.id]: {
            text: 'Active Repair List',
            size: 22,
            align: 'left',
          },
          [repairList.id]: {
            dateId: null,
            columns: {
              gridLocation: true,
              level: true,
              category: true,
              coords: false,
              notes: false,
              timestamp: false,
            },
            margin: 12,
          },
        },

        sectionProps: { COVER: {} },
        notes: '',
        siteName,
        selectedDate: date,
      };
    },
  },

  {
    key: 'blank',
    label: '+ ',
    initial: (siteName, date) => ({
      name: 'Untitled Report',
      order: [],
      sectionPropsById: {},
      sectionProps: { COVER: {} },
      notes: '',
      siteName,
      selectedDate: date,
    }),
  },
];

const normalizeIB = (id) => {
  const s = String(id || '').toUpperCase();
  const m = s.match(/(\d+)/);
  return m ? `IB${m[1]}` : (s || 'IB?');
};

function makeBlockBundle(ibKey) {
  const t = makeInst('TITLE');
  const m = makeInst('MAP');
  const tbl = makeInst('FAULT_CHART');
  const blockNumber = ibKey.replace(/^IB/i, '');

  const props = {
    [t.id]: {
      text: `Fault Map – ${blockNumber}`,
      size: 26,
      align: 'left',
    },
    [m.id]: {
      marginTop: 8,
      marginBottom: 12,
      size: 'large',
      showFaults: true,
      showPanelsInBlocks: true,
      mode: 'blocks',
      blocksPerRow: 1,
      selectedBlocks: [ibKey, ibKey.replace(/^IB/i, '')],
    },
    [tbl.id]: {
      blocks: { [ibKey]: true, [ibKey.replace(/^IB/i, '')]: true },
      levels: { 'Level 1': true, 'Level 2': true, 'Level 3': true },
    },
  };

  return { orderItems: [t, m, tbl], props };
}


function parseDateFromString(s) {
  if (!s) return null;
  if (s instanceof Date) return s;
  if (typeof s !== 'string') return null;

  const trimmed = s.trim();

  if (trimmed.includes('-') || trimmed.includes('/')) {
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }

  const m = trimmed.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (m) {
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    const y = parseInt(m[3], 10);

    // If the first part > 12, treat as DMY, else treat as MDY
    const isDMY = a > 12;
    const M = isDMY ? b : a;
    const D = isDMY ? a : b;

    const d = new Date(y, M - 1, D);
    return isNaN(d.getTime()) ? null : d;
  }

  // Fallback
  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function pad2(n) {
  return String(n).padStart(2, '0');
}

// style: 'numeric' | 'long'
// order: 'DMY' | 'MDY' | 'YMD'
function formatDateForDisplay(dateObj, { style = 'numeric', order = 'MDY' } = {}) {
  if (!dateObj) return '—';

  const D = dateObj.getDate();
  const M = dateObj.getMonth() + 1;
  const Y = dateObj.getFullYear();

  if (style === 'numeric') {
    if (order === 'DMY') return `${pad2(D)}-${pad2(M)}-${Y}`;
    if (order === 'YMD') return `${Y}-${pad2(M)}-${pad2(D)}`;
    // MDY default
    return `${pad2(M)}-${pad2(D)}-${Y}`;
  }

  // long
  const month = MONTH_NAMES[M - 1];
  if (order === 'DMY') return `${pad2(D)} ${month} ${Y}`;
  if (order === 'YMD') return `${Y} ${month} ${pad2(D)}`;
  // MDY default
  return `${month} ${pad2(D)}, ${Y}`;
}

const DEFAULT_OVERVIEW_PROPS = {
  layout: 'grid',
  size: 'md',
  align: 'left',
  margin: 8,
  showTotal: true,
  showAffected: true,
  showPercent: true,
  showKwh: true,
  labelTotal: 'Total Panels',
  labelAffected: 'Affected',
  labelPercent: '% Affected',
  labelKwh: 'Est. kWh Lost',
  percentDecimals: 2,
  kwhDecimals: 0,
  alertPercentOver: null,
};

const DEFAULT_CUSTOM_TEXT_PROPS = {
  text: '',
  size: 16,
  weight: 400,
  align: 'left',
  marginTop: 6,
  marginBottom: 12,
  lineHeight: 1.4,
  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
};

//Format helper
const fmt = (num, decimals = 0) =>
  (typeof num === 'number' && !Number.isNaN(num))
    ? num.toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals })
    : String(num ?? '—');

async function pvwattsAnnual(lat, lng, systemKW) {
  if (!lat || !lng || !systemKW || !PVWATTS_API_KEY) return null;

  const params = new URLSearchParams({
    api_key: PVWATTS_API_KEY,
    lat: String(lat),
    lon: String(lng),
    system_capacity: String(systemKW),
    module_type: '0',
    losses: '10',
    array_type: '2',
    tilt: '0',
    azimuth: '180',
  });

  try {
    const res = await fetch(`${PVWATTS_API_URL}?${params.toString()}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data?.outputs?.ac_annual ?? null;
  } catch {
    return null;
  }
}

// Stuff to cache the PV Watts Data
const PV_CACHE_KEY = 'pvw_cache_v1';

function pvRound(n, d = 3) { return Number.isFinite(n) ? Number(n.toFixed(d)) : n; }

function pvKey({ site, date, lat, lng, kw }) {
  return [
    site || '—',
    date || '—',
    pvRound(lat, 3),
    pvRound(lng, 3),
    pvRound(kw, 3),
  ].join('|');
}

function readPvCache() {
  try { return JSON.parse(localStorage.getItem(PV_CACHE_KEY) || '{}'); }
  catch { return {}; }
}

function writePvCache(obj) {
  try { localStorage.setItem(PV_CACHE_KEY, JSON.stringify(obj)); } catch { }
}

//END OF TOP LEVEL SHIETTTTTTT

export default function DataExporter() {
  const pageRef = useRef(null);
  const { siteName: routeSite = '', selectedDate: routeDate = '' } = useParams();
  const [pageView, setPageView] = useState('report');
  const [paper, setPaper] = useState('A4');
  const SIZES = {
    A4: { w: '210mm', h: '297mm', page: '210mm 297mm' },
    LETTER: { w: '8.5in', h: '11in', page: '8.5in 11in' },
  };

  //Export page stuff
  const mm = v => parseFloat(v.replace('mm', ''));
  const inch = v => parseFloat(v.replace('in', ''));
  const toMm = v => v.endsWith('mm') ? mm(v) : inch(v) * 25.4;
  const reportPageCountRef = useRef(0);

  const BLOCK_GAP = 2;

  const [exportMode, setExportMode] = useState(false);
  const [includeCover, setIncludeCover] = useState(true);
  const [includeTOC, setIncludeTOC] = useState(true);
  const [layoutResetKey, setLayoutResetKey] = useState(0);

  const [exporting, setExporting] = useState(false);

  // Local templates (session only)
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const active = useMemo(() => templates.find(t => t.id === activeId) || null, [templates, activeId]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [closeCoverToolsTick, setCloseCoverToolsTick] = useState(0);

  const hasKind = React.useCallback(
    (k) => (active?.order || []).some(x => x.kind === k),
    [active?.order]
  );

  const [siteMeta, setSiteMeta] = useState(null);
  const [resolvedDate, setResolvedDate] = useState('');
  const [overview, setOverview] = useState(null);
  const [faults, setFaults] = useState(null);
  const [faultChart, setFaultChart] = useState([]);
  const [repairList, setRepairList] = useState([]);
  const pvCacheRef = useRef(readPvCache());

  const [orthoData, setOrthoData] = useState(null);

  // 'fetchOverlayData' is from firebase but setOrthoData is defined here but for the same things
  useEffect(() => {
    if (!routeSite || !hasKind('ORTHO')) return;
    let mounted = true;
    (async () => {
      try {
        const data = await fetchOverlayData(routeSite);
        if (mounted) setOrthoData(data);
      } catch (error) {
        console.error("Failed to load overlay data for export:", error);
        if (mounted) setOrthoData(null);
      }
    })();
    return () => { mounted = false; };
  }, [routeSite, hasKind]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!routeSite) return;
      const meta = await fetchSiteDataDL(routeSite);
      if (!mounted) return;
      setSiteMeta(meta);

      const d = routeDate || (await fetchLatestDate(routeSite));
      if (!mounted) return;
      setResolvedDate(d || '');
    })();
    return () => { mounted = false; };
  }, [routeSite, routeDate]);

  // Default blank template
  useEffect(() => {
    if (!routeSite || !resolvedDate) return;
    if (templates.length > 0) return;

    const blank = PRESETS.find(p => p.key === 'blank');
    if (!blank) return;

    const base = blank.initial(routeSite, resolvedDate);
    const now = new Date().toISOString();
    const doc = { id: uid(), createdAt: now, updatedAt: now, ...base };
    setTemplates([doc]);
    setActiveId(doc.id);
  }, [routeSite, resolvedDate, templates.length]);

  // Actions
  const createFromPreset = (presetKey) => {
    const p = PRESETS.find(x => x.key === presetKey);
    if (!p) return;
    const t = p.initial(routeSite, resolvedDate);
    const now = new Date().toISOString();
    const doc = { id: uid(), updatedAt: now, createdAt: now, ...t };
    setTemplates(prev => [doc, ...prev]);
    setActiveId(doc.id);
  };

  const deleteFromHistory = (id) => {
    setTemplates(prev => {
      const next = prev.filter(t => t.id !== id);
      if (id === activeId) setActiveId(next[0]?.id || null);
      return next;
    });
  };

  const addItem = (kind) => {
    if (!active) return;
    const inst = { id: uid(), kind };
    setTemplates(prev =>
      prev.map(t =>
        t.id === active.id
          ? {
            ...t,
            order: [...(t.order || []), inst],
            updatedAt: new Date().toISOString()
          }
          : t
      )
    );
  };

  const { ibs } = useMapData(active);
  const availableBlocks = React.useMemo(() => {
    const keys = ibs ? Object.keys(ibs) : [];
    const unique = Array.from(new Set(keys.map(normalizeIB)));
    unique.sort((a, b) => {
      const na = parseInt(a.replace(/\D/g, ''), 10) || 0;
      const nb = parseInt(b.replace(/\D/g, ''), 10) || 0;
      return na - nb || a.localeCompare(b);
    });
    return unique;
  }, [ibs]);

  const blocksWithFaults = React.useMemo(() => {
    if (!active?.meta?.onlyBlocksWithFaults) return availableBlocks;
    if (!Array.isArray(faultChart) && !Array.isArray(repairList) && !Array.isArray(faults)) return availableBlocks;
    const list = Array.isArray(faults) ? faults : [];
    const set = new Set();
    list.forEach(f => {
      const loc = f.location || f.panelId || '';
      const ib = normalizeIB(loc);
      if (ib) set.add(ib);
    });
    return availableBlocks.filter(ib => set.has(ib));
  }, [availableBlocks, active?.meta?.onlyBlocksWithFaults, faults]);


  React.useEffect(() => {
    const doc = active;
    if (!doc) return;
    if (!doc.meta?.autoExpandBlocks) return;
    if (doc.meta?.blocksExpanded) return;
    if (!blocksWithFaults.length) return;

    const cap = Math.max(1, Number(doc.meta?.blocksLimit ?? 999));
    const pick = blocksWithFaults.slice(0, cap);

    const bundles = pick.map(ib => makeBlockBundle(ib));
    const extraOrder = bundles.flatMap(b => b.orderItems);
    const extraProps = Object.assign({}, ...bundles.map(b => b.props));
    const nextOrder = [...(doc.order || []), ...extraOrder];

    setTemplates(prev => prev.map(t => {
      if (t.id !== doc.id) return t;
      return {
        ...t,
        order: nextOrder,
        sectionPropsById: { ...(t.sectionPropsById || {}), ...extraProps },
        meta: { ...(t.meta || {}), blocksExpanded: true },
        updatedAt: new Date().toISOString(),
      };
    }));
  }, [active, blocksWithFaults, setTemplates]);

  const move = (id, dir) => {
    if (!active) return;
    const arr = [...(active.order || [])];
    const i = arr.findIndex(x => x.id === id);
    if (i < 0) return;
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setTemplates(prev =>
      prev.map(t => t.id === active.id ? { ...t, order: arr, updatedAt: new Date().toISOString() } : t)
    );
  };

  const removeFromOrder = (id) => {
    if (!active) return;
    const order = (active.order || []).filter(x => x.id !== id);
    setTemplates(prev =>
      prev.map(t => t.id === active.id ? { ...t, order, updatedAt: new Date().toISOString() } : t)
    );
  };

  const updateNotes = (val) => {
    if (!active) return;
    setTemplates(prev =>
      prev.map(t => t.id === active.id ? { ...t, notes: val, updatedAt: new Date().toISOString() } : t)
    );
  };

  const MeasureColumn = React.forwardRef(function MeasureColumn(
    { sizes, paper, active, overview, faultChart, repairList, updateSectionProps, padding, siteName },
    ref
  ) {
    const innerH = React.useRef(0);
    const pageStyle = {
      width: `var(--page-w)`,
      height: `var(--page-h)`,
      position: 'relative',
      boxSizing: 'border-box',
      padding: `${padding}px`,
    };

    React.useEffect(() => {
      if (!ref.current) return;
      const page = ref.current.querySelector('.measure-page');
      if (!page) return;
      const rect = page.getBoundingClientRect();
      const inner = rect.height - (padding * 2);
      ref.current.setAttribute('data-pageh', String(inner));
    }, [padding, paper, sizes]);

    return (
      <div ref={ref}>
        <div
          className="measure-page"
          style={{
            ...pageStyle,
            '--page-w': sizes[paper].w,
            '--page-h': sizes[paper].h,
            width: sizes[paper].w,
            height: sizes[paper].h,
          }}>
          {(active.order || []).map((inst, idx) => (
            <div
              key={inst.id}
              data-measure-id={inst.id}
              data-kind={inst.kind}
              data-break-policy={BREAK_POLICY[inst.kind] || 'keep'}
              style={{ marginBottom: BLOCK_GAP }}
            >
              <SectionPreview
                kind={inst.kind}
                instanceId={inst.id}
                enabled={true}
                active={active}
                overview={overview}
                faultChart={faultChart}
                repairList={repairList}
                updateSectionProps={updateSectionProps}
                faults={faults}
                inExport={true}
                siteName={siteName}
              />
            </div>
          ))}
        </div>
      </div>
    );
  });

  //data loaders

  //KPI OVERVIEW LOADER
  useEffect(() => {
    if (!active || !hasKind('OVERVIEW') || !routeSite || !resolvedDate) return;
    let mounted = true;
    (async () => {
      const faultsArr = await fetchFaultDataDL(routeSite, resolvedDate);
      if (!mounted) return;
      setFaults(faultsArr);

      const totalPanels = Number(siteMeta?.totalPanels ?? 0);

      const affectedPanels = Array.isArray(faultsArr)
        ? faultsArr.reduce((sum, f) => sum + (Number(f.panelCount) || 1), 0)
        : 0;

      const percentAffected = totalPanels
        ? Number(((affectedPanels / totalPanels) * 100).toFixed(2))
        : 0;

      setOverview({
        totalPanels,
        affectedPanels,
        percentAffected,
        kwhLost: undefined,
      });
    })();
    return () => { mounted = false; };
  }, [active, hasKind, routeSite, resolvedDate, siteMeta]);

  // Ensure faults are available whenever a MAP tile is used
  useEffect(() => {
    if (!routeSite || !resolvedDate) return;
    if (!hasKind('MAP')) return;

    let cancelled = false;

    (async () => {
      try {
        // if already loaded for another section, keep it
        if (Array.isArray(faults) && faults.length) return;

        const f = await fetchFaultDataDL(routeSite, resolvedDate);
        if (!cancelled) setFaults(f || []);
      } catch (e) {
        console.error('Failed to load faults for map:', e);
        if (!cancelled) setFaults([]);
      }
    })();

    return () => { cancelled = true; };
  }, [routeSite, resolvedDate, hasKind,]);


  const lastPvReqRef = React.useRef({ lat: null, lng: null, kw: null });

  useEffect(() => {
    if (!siteMeta || !overview) return;

    const lat = siteMeta?.location?.latitude;
    const lng = siteMeta?.location?.longitude;
    const kWPerPanel = Number(siteMeta?.customFields?.panelProd ?? 0);
    const affectedPanels = Number(overview?.affectedPanels ?? 0);

    if (!lat || !lng || !kWPerPanel || !affectedPanels) return;

    const systemKW = affectedPanels * kWPerPanel;
    const key = pvKey({ site: routeSite, date: resolvedDate, lat, lng, kw: systemKW });

    const cached = pvCacheRef.current[key];
    if (typeof cached === 'number' && overview.kwhLost !== cached) {
      setOverview(prev => prev ? { ...prev, kwhLost: cached } : prev);
    }

    const last = lastPvReqRef.current;
    if (last.lat === lat && last.lng === lng && last.kw === systemKW) return;

    let cancelled = false;
    (async () => {
      const acAnnual = await pvwattsAnnual(lat, lng, systemKW);
      if (cancelled || typeof acAnnual !== 'number') return;

      lastPvReqRef.current = { lat, lng, kw: systemKW };

      setOverview(prev => prev ? { ...prev, kwhLost: acAnnual } : prev);

      pvCacheRef.current[key] = acAnnual;
      writePvCache(pvCacheRef.current);
    })();

    return () => { cancelled = true; };
  }, [siteMeta, overview, routeSite, resolvedDate]);

  useEffect(() => {
    if (!active || !hasKind('FAULT_CHART') || !routeSite || !resolvedDate) return;
    let mounted = true;
    (async () => {
      const f = faults || (await fetchFaultDataDL(routeSite, resolvedDate));
      if (!mounted) return;
      if (!faults) setFaults(f);
      const counts = {};
      (f || []).forEach(it => {
        const lvl = String(it.level || 'Unknown');
        counts[lvl] = (counts[lvl] || 0) + 1;
      });
      setFaultChart(Object.entries(counts).map(([level, count]) => ({ level, count })));
    })();

    return () => { mounted = false; };
  }, [hasKind, routeSite, resolvedDate]);

  useEffect(() => {
    if (!active || !hasKind('REPAIR_LIST') || !routeSite || !resolvedDate) return;
    let mounted = true;
    (async () => {
      const list = await fetchPanelsForRepairDate(routeSite, resolvedDate);
      if (!mounted) return;
      setRepairList(list || []);
    })();
    return () => { mounted = false; };
  }, [hasKind, routeSite, resolvedDate]);

  const updateSectionProps = (instanceId, partial) => {
    if (!active) return;
    setTemplates(prev =>
      prev.map(t =>
        t.id === active.id
          ? {
            ...t,
            sectionPropsById: {
              ...(t.sectionPropsById || {}),
              [instanceId]: {
                ...(t.sectionPropsById?.[instanceId] || {}),
                ...partial,
              },
            },
            updatedAt: new Date().toISOString(),
          }
          : t
      )
    );
  };

  const updateCoverProps = (partial) => {
    if (!active) return;
    setTemplates(prev =>
      prev.map(t =>
        t.id === active.id
          ? {
            ...t,
            sectionProps: {
              ...(t.sectionProps || {}),
              COVER: { ...(t.sectionProps?.COVER || {}), ...partial },
            },
            updatedAt: new Date().toISOString(),
          }
          : t
      )
    );
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-print-size', '');
    style.textContent = `
    @media print {
      @page { size: ${SIZES[paper].page}; margin: 12mm; }
    }
  `;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, [paper]);

  useEffect(() => {
    const clear = () => setExportMode(false);
    window.addEventListener('afterprint', clear);
    return () => window.removeEventListener('afterprint', clear);
  }, []);

  //part of the PV Watt cache feature set
  useEffect(() => {
    if (!siteMeta || !overview) return;

    const lat = siteMeta?.location?.latitude;
    const lng = siteMeta?.location?.longitude;
    const kWPerPanel = Number(siteMeta?.customFields?.panelProd ?? 0);
    const affectedPanels = Number(overview?.affectedPanels ?? 0);
    if (!lat || !lng || !kWPerPanel || !affectedPanels) return;

    const systemKW = affectedPanels * kWPerPanel;
    const key = pvKey({ site: routeSite, date: resolvedDate, lat, lng, kw: systemKW });
    const cached = pvCacheRef.current[key];

    if (typeof cached === 'number' && overview.kwhLost == null) {
      setOverview(prev => prev ? { ...prev, kwhLost: cached } : prev);
    }
  }, [siteMeta, overview, routeSite, resolvedDate]);

  async function bakeMapSnapshotsForActiveDoc(active, updateSectionProps) {
    if (!active) return;
    await new Promise(r => setTimeout(r, 16));

    const mapInstances = (active.order || []).filter(x => x.kind === 'MAP');
    for (const inst of mapInstances) {
      const root = document.querySelector(
        `.preview-board [data-instance-id="${inst.id}"][data-map-root], .preview-board [data-instance-id="${inst.id}"] [data-map-root]`
      );
      if (!root) continue;
      const svgHost = root.querySelector('svg');
      if (!svgHost) continue;
      const wrapper = document.createElement('div');
      const originalParent = svgHost.parentNode;
      const clone = originalParent.cloneNode(true);
      clone.querySelectorAll('button,input,textarea,select').forEach(n => n.remove());
      wrapper.appendChild(clone);

      const snapshotHtml = wrapper.innerHTML;
      updateSectionProps(inst.id, { snapshotSvg: snapshotHtml });
    }
  }

  async function bakeRepairListForActiveDoc(active, siteName, updateSectionProps) {
    if (!active) return;
    const instances = (active.order || []).filter(x => x.kind === 'REPAIR_LIST');
    for (const inst of instances) {
      const p = active.sectionPropsById?.[inst.id] || {};
      if (!p.dateId) continue;
      try {
        const rows = await hydrateRepairList(siteName, p.dateId); // your current hydrator signature
        updateSectionProps(inst.id, { _hydratedRows: rows });
      } catch {
        updateSectionProps(inst.id, { _hydratedRows: [] });
      }
    }
  }

  const handleExport = async () => {
    if (!active) return;

    try {
      setExporting(true);
      setCloseCoverToolsTick(t => t + 1);
      setLayoutResetKey(k => k + 1);
      setExportMode(true);

      await bakeMapSnapshotsForActiveDoc(active, updateSectionProps);
      await bakeRepairListForActiveDoc(active, routeSite, updateSectionProps);

      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 16));
        const ready = document.querySelectorAll('.export-doc .export-page.export-page--print').length > 0;
        if (ready) break;
      }

      let stableHits = 0;
      for (let i = 0; i < 300; i++) {
        await new Promise(r => setTimeout(r, 16));
        const cont = document.querySelector('.export-doc');
        if (!cont) continue;

        const expected =
          (includeCover ? 1 : 0) +
          (includeTOC ? 1 : 0) +
          (reportPageCountRef.current || 0);

        const pagesNow = cont.querySelectorAll('.export-page.export-page--print').length;
        if (expected > 0 && pagesNow === expected) {
          stableHits++;
          if (stableHits >= 5) break;
        } else {
          stableHits = 0;
        }
      }

      const container = document.querySelector('.export-doc');
      if (!container) throw new Error('Export container not found');

      const pages = Array.from(
        container.querySelectorAll('.export-page.export-page--print')
      );

      if (!pages.length) throw new Error('No pages to export');

      const pageWmm = toMm(SIZES[paper].w);
      const pageHmm = toMm(SIZES[paper].h);
      const doc = new jsPDF({
        unit: 'mm',
        format: [pageWmm, pageHmm],
        compress: true,
      });

      const scale = Math.max(3, window.devicePixelRatio || 1);

      for (let i = 0; i < pages.length; i++) {
        const node = pages[i];

        const canvas = await html2canvas(node, {
          backgroundColor: '#fdfcf6',
          scale,
          useCORS: true,
          letterRendering: true,
          imageTimeout: 0,
        });

        const imgData = canvas.toDataURL('image/png');
        if (i > 0) doc.addPage([pageWmm, pageHmm]);
        doc.addImage(imgData, 'PNG', 0, 0, pageWmm, pageHmm);
      }

      const dateSafe = (resolvedDate || '').replace(/[^\d-]/g, '') || new Date().toISOString().slice(0, 10);
      const siteSafe = (routeSite || 'report').replace(/[^\w-]+/g, '_');
      doc.save(`${siteSafe}_${dateSafe}.pdf`);
    } catch (err) {
      console.error(err);
      alert('Export failed. Check console for details.');
    } finally {
      setExporting(false);
      setExportMode(false);
    }
  };

  function ExportDoc(props) {
    const {
      paper, sizes, includeCover, includeTOC,
      active, routeSite, resolvedDate,
      overview, faultChart, repairList,
      updateSectionProps, onLayoutReady
    } = props;

    const Page = ({ children }) => (
      <div
        className="export-page export-page--print"
        style={{
          '--page-w': sizes[paper].w,
          '--page-h': sizes[paper].h,
          width: sizes[paper].w,
          height: sizes[paper].h,
        }}
      >
        {children}
      </div>
    );

    return (
      <div className="export-doc">
        {/*Title page decleration */}
        {includeCover && (
          <Page>
            <CoverPreview
              mode="export"
              active={active}
              routeSite={routeSite}
              resolvedDate={resolvedDate}
              updateCoverProps={updateCoverProps}
            />
          </Page>
        )}

        {/*table of contents declaration */}
        {includeTOC && (
          <Page>
            <TOCPreview
              active={active}
              routeSite={routeSite}
              resolvedDate={resolvedDate}
            />
          </Page>
        )}

        {/*actual report part */}
        <ReportFlow
          sizes={sizes}
          paper={paper}
          active={active}
          overview={overview}
          faultChart={faultChart}
          repairList={repairList}
          updateSectionProps={updateSectionProps}
          resetKey={layoutResetKey}
          onLayoutReady={props.onLayoutReady}
          routeSite={routeSite}
        />
      </div>
    );
  }

  

  function ReportFlow({
    sizes, paper,
    active, overview, faultChart, repairList, updateSectionProps, resetKey, onLayoutReady, routeSite,
  }) {
    const PAGE_PADDING = 24;

    const measureRef = React.useRef(null);
    const [layout, setLayout] = React.useState(null);

    useEffect(() => {
      setLayout(null);
    }, [resetKey]);

    const order = active.order || [];
    React.useEffect(() => {
      const el = measureRef.current;
      if (!el) return;

      const pageHpx = el.dataset.pageh ? Number(el.dataset.pageh) : 0;
      if (!pageHpx) return;

      const blocks = Array.from(el.querySelectorAll('[data-measure-id]')).map(node => {
        const id = node.getAttribute('data-measure-id');
        const kind = node.getAttribute('data-kind');
        const rect = node.getBoundingClientRect();
        const style = getComputedStyle(node);
        const mt = parseFloat(style.marginTop) || 0;
        const mb = parseFloat(style.marginBottom) || 0;
        const totalH = rect.height + mt + mb;

        let rows = null, headerH = 0;
        const table = node.querySelector('table[data-splittable="rows"]');
        if (table) {
          const thead = table.querySelector('thead');
          headerH = thead ? thead.getBoundingClientRect().height : 0;
          rows = Array.from(table.querySelectorAll('tbody tr')).map(tr =>
            tr.getBoundingClientRect().height
          );
        }
        const policy = node.getAttribute('data-break-policy') || 'keep';
        return { id, kind, policy, totalH, headerH, rows };
      });

      const pages = [];
      let current = [];
      let remaining = pageHpx;

      const pushPage = () => { pages.push(current); current = []; remaining = pageHpx; };
      const pushBlock = (block) => { current.push(block); remaining -= block.totalH; };

      for (const b of blocks) {
        if (b.totalH <= remaining || b.policy === 'keep') {
          if (b.totalH > remaining && current.length) pushPage();
          pushBlock(b);
          continue;
        }

        if (b.policy === 'split' && Array.isArray(b.rows) && b.rows.length) {
          const header = b.headerH;
          const rowHeights = b.rows;
          let start = 0;

          while (start < rowHeights.length) {
            if (remaining < header + 8) {
              if (current.length) pushPage();
            }

            let acc = header;
            let end = start;
            while (end < rowHeights.length && acc + rowHeights[end] <= remaining) {
              acc += rowHeights[end];
              end++;
            }

            if (end === start) end = start + 1;
            current.push({
              ...b,
              slice: { start, end },
              totalH: acc,
            });
            remaining -= acc;
            start = end;

            if (start < rowHeights.length) pushPage();
          }
          continue;
        }

        if (current.length) pushPage();
        pushBlock(b);
      }

      if (current.length) pages.push(current);
      setLayout(pages);
      onLayoutReady?.(pages.length);

    }, [active?.id, active?.order, faultChart, repairList, sizes, paper, resetKey]);

    if (!layout) {
      return (
        <div style={{ position: 'absolute', left: -99999, top: 0 }}>
          <MeasureColumn
            sizes={sizes} paper={paper}
            active={active}
            overview={overview}
            faultChart={faultChart}
            repairList={repairList}
            updateSectionProps={updateSectionProps}
            ref={measureRef}
            padding={PAGE_PADDING}
            siteName={routeSite}
          />
        </div>
      );
    }
    return (
      <>
        {layout.map((pageBlocks, i) => (
          <div
            key={i}
            className="export-page export-page--print"
            style={{ '--page-w': sizes[paper].w, '--page-h': sizes[paper].h }}
          >
            <div style={{ padding: PAGE_PADDING }}>
              {pageBlocks.map((b) => (
                <ReportBlockRenderer
                  key={`${b.id}:${b.slice ? `${b.slice.start}-${b.slice.end}` : 'full'}`}
                  block={b}
                  active={active}
                  overview={overview}
                  faultChart={faultChart}
                  repairList={repairList}
                  updateSectionProps={updateSectionProps}
                  routeSite={routeSite}
                />
              ))}
            </div>
          </div>
        ))}
      </>
    );
  }

  function ReportBlockRenderer({
    block, active, overview, faultChart, repairList, updateSectionProps, routeSite
  }) {
    const kind = block.kind;
    const exportSlice = block.slice ? { start: block.slice.start, end: block.slice.end } : undefined;

    return (
      <div style={{ marginBottom: BLOCK_GAP }}>
        <SectionPreview
          kind={kind}
          instanceId={block.id}
          enabled={true}
          active={active}
          overview={overview}
          faultChart={faultChart}
          repairList={repairList}
          updateSectionProps={updateSectionProps}
          exportSlice={exportSlice}
          faults={faults}
          inExport={true}
          siteName={routeSite}
        />
      </div>
    );
  }

  // UI
  return (
    <div className="exporter new-layout two-col">
      {/* LEFT COLUMN */}
      <div className="left-col">
        <h1 className="page-title">Document Export</h1>

        {/* Page picker */}
        <div className="page-pills">
          <button
            className={`pill ${pageView === 'cover' ? 'on' : ''}`}
            onClick={() => setPageView('cover')}
          >
            Title page
          </button>
          <button
            className={`pill ${pageView === 'toc' ? 'on' : ''}`}
            onClick={() => setPageView('toc')}
          >
            Table of contents
          </button>
          <button
            className={`pill ${pageView === 'report' ? 'on' : ''}`}
            onClick={() => setPageView('report')}
          >
            Report
          </button>
        </div>

        {/*Template BAWX */}
        <div className="templates-box">
          <div className="box-title">Start A New Session</div>
          <div className="preset-grid">
            {PRESETS.map(p => (
              <button key={p.key} className="preset-btn" onClick={() => createFromPreset(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="context">
            <div><span className="muted">Site:</span> <span className="mono">{routeSite || '—'}</span></div>
            <div><span className="muted">Date:</span> <span className="mono">{resolvedDate || '—'}</span></div>
          </div>
        </div>

        {/* "Clear Browser history" */}
        <aside className="history-sidebar inset">
          <div className="history-title">Your Sessions</div>
          <ul className="history-list">
            {templates.map(t => (
              <li key={t.id} className={t.id === activeId ? 'active' : ''}>
                <button className="history-item" onClick={() => setActiveId(t.id)}>
                  <div className="name">{t.name}</div>
                  <div className="sub">{new Date(t.updatedAt).toLocaleString()}</div>
                </button>
                <button className="history-del" onClick={() => deleteFromHistory(t.id)} title="Delete">×</button>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {exportMode && (
        <ExportDoc
          paper={paper}
          sizes={SIZES}
          includeCover={includeCover}
          includeTOC={includeTOC}
          active={active}
          routeSite={routeSite}
          resolvedDate={resolvedDate}
          overview={overview}
          faultChart={faultChart}
          repairList={repairList}
          updateSectionProps={updateSectionProps}
          onLayoutReady={(n) => { reportPageCountRef.current = n; }}
        />
      )}

      <div className="right-col">
        <div className="actions-row">
          <div className="item-picker">
            <div className="box-title">Add items</div>
            <div className="chips">
              {SECTION_CATALOG.map(s => {
                const count = (active?.order || []).filter(i => i.kind === s.key).length;
                return (
                  <button
                    key={s.key}
                    className="chip"
                    onClick={() => addItem(s.key)}
                    title={count ? `${count} added` : 'Add'}
                  >
                    {s.label}{count ? ` (${count})` : ''}
                  </button>
                );
              })}
            </div>
            <div className="hint">Click to add; manage order/removal in the preview below.</div>
          </div>

          {/* Export CTA */}
          <div className="export-cta">
            <button
              className="export-btn"
              onClick={handleExport}
              disabled={!active || exporting}
            >
              {exporting ? 'Exporting…' : 'Export Report'}
            </button>

            <div className="export-opts">
              <span className="export-opts-label">Include:</span>

              <button
                type="button"
                className={`export-opt ${includeCover ? 'on' : ''}`}
                onClick={() => setIncludeCover(!includeCover)}
              >
                Title page
              </button>

              <button
                type="button"
                className={`export-opt ${includeTOC ? 'on' : ''}`}
                onClick={() => setIncludeTOC(!includeTOC)}
              >
                Table of contents
              </button>
            </div>
          </div>

        </div>

        {/* Page preview board */}
        <div className="preview-board">
          <div className="preview-head">Page preview</div>

          <div className="export-page"
            ref={pageRef}
            onClick={() => setSelectedKey(null)}
            style={{ '--page-w': SIZES[paper].w, '--page-h': SIZES[paper].h, }}
          >
            {!active ? (
              <div className="placeholder-big">Create or select a template on the left</div>
            ) : pageView === 'cover' ? (
              <CoverPreview
                active={active}
                routeSite={routeSite}
                resolvedDate={resolvedDate}
                updateCoverProps={updateCoverProps}
                closeToolsTick={closeCoverToolsTick}
              />
            ) : pageView === 'toc' ? (
              <TOCPreview
                active={active}
                routeSite={routeSite}
                resolvedDate={resolvedDate}
              />
            ) : (
              <div className="report-canvas" style={{ '--page-w': SIZES[paper].w, '--page-h': SIZES[paper].h }}>
                {(active.order || []).map(inst => (
                  <SectionBlock
                    key={inst.id}
                    instance={inst}
                    kind={inst.kind}
                    enabled={true}
                    active={active}
                    overview={overview}
                    faultChart={faultChart}
                    repairList={repairList}
                    faults={faults}
                    orthoData={orthoData}
                    onNotesChange={updateNotes}
                    onMoveUp={() => move(inst.id, 'up')}
                    onMoveDown={() => move(inst.id, 'down')}
                    onRemove={() => removeFromOrder(inst.id)}
                    updateSectionProps={updateSectionProps}
                    isActive={selectedKey === inst.id}
                    onActivate={() => setSelectedKey(inst.id)}
                    routeSite={routeSite}
                    siteName={routeSite}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function extractBlockId(location) {
  if (!location || typeof location !== 'string') return null;
  const dashIndex = location.indexOf('-');
  const prefix = dashIndex !== -1 ? location.substring(0, dashIndex).toUpperCase() : null;

  if (prefix && prefix.startsWith('IB')) {
    return `Block ${prefix.substring(2)}`;
  }
  return null;
}

function OrthoEditor({ instanceId, active, orthoData, updateSectionProps, onClose }) {
  const availableCategories = orthoData ? Object.keys(orthoData) : [];
  const p = active?.sectionPropsById?.[instanceId] || {};
  const selectedCategory = p.category;

  const imageRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const imageDimensionsRef = React.useRef({ width: 0, height: 0 });
  const fitScaleRef = React.useRef(1);

  const [panX, setPanX] = React.useState(p.panX || 0);
  const [panY, setPanY] = React.useState(p.panY || 0);
  const [isPanning, setIsPanning] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const [imageUrl, setImageUrl] = React.useState(null);
  const [rotation, setRotation] = React.useState(p.rotation || 0);

  const [scale, setScale] = React.useState(p.scale || 1);

  const [saving, setSaving] = React.useState(false);
  const [savedPulse, setSavedPulse] = React.useState(false);

  const [relativeZoom, setRelativeZoom] = React.useState(1);

  const VIEWER_SIZE = 650;
  const viewerRef = React.useRef(null);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsPanning(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    e.preventDefault();
    if (!isPanning) return;

    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;

    setPanX(prev => prev + dx);
    setPanY(prev => prev + dy);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleImageDragStart = (e) => e.preventDefault();

  const resetView = () => {

    setScale(fitScaleRef.current);
    setRelativeZoom(1);
    setRotation(0);

    const imageWidth = imageDimensionsRef.current.width;
    const scaledWidth = imageWidth * fitScaleRef.current;
    const scaledHeight = imageDimensionsRef.current.height * fitScaleRef.current;

    setPanX((VIEWER_SIZE - scaledWidth) / 2);
    setPanY((VIEWER_SIZE - scaledHeight) / 2);
  };

  // Ghost square to show size preview
  const [showSizeGhost, setShowSizeGhost] = React.useState(false);
  const ghostHideTimerRef = React.useRef(null);

  const pingGhost = React.useCallback(() => {
    setShowSizeGhost(true);
    if (ghostHideTimerRef.current) clearTimeout(ghostHideTimerRef.current);
    ghostHideTimerRef.current = setTimeout(() => setShowSizeGhost(false), 700);
  }, []);

  React.useEffect(() => {
    return () => {
      if (ghostHideTimerRef.current) clearTimeout(ghostHideTimerRef.current);
    };
  }, []);

  //listener for actual image rotation, zoom, and movement
  React.useEffect(() => {
    if (!selectedCategory || !orthoData || !orthoData[selectedCategory]?.image) {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
      setImageUrl(null);
      imageDimensionsRef.current = { width: 0, height: 0 };
      setPanX(0);
      setPanY(0);
      setScale(1);
      setRotation(0);
      return;
    }

    const imageBlob = orthoData[selectedCategory].image;
    const url = URL.createObjectURL(imageBlob);
    setImageUrl(url);
    const revoke = () => URL.revokeObjectURL(url);

    const img = new Image();
    img.decoding = 'sync';
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageDimensionsRef.current = { width: img.naturalWidth, height: img.naturalHeight };

      const imageWidth = img.naturalWidth;
      const imageHeight = img.naturalHeight;

      const scaleX = VIEWER_SIZE / imageWidth;
      const scaleY = VIEWER_SIZE / imageHeight;
      const initialScale = Math.min(scaleX, scaleY);
      fitScaleRef.current = initialScale;

      const scaledWidth = imageWidth * initialScale;
      const scaledHeight = imageHeight * initialScale;

      const initialPanX = (VIEWER_SIZE - scaledWidth) / 2;
      const initialPanY = (VIEWER_SIZE - scaledHeight) / 2;

      if (p.scale === undefined) {
        setScale(initialScale);
        setRelativeZoom(1);
        setRotation(0);

        setPanX(initialPanX);
        setPanY(initialPanY);
      } else {
        setRelativeZoom(p.scale / initialScale);
        setRotation(p.rotation);
        setPanX(p.panX);
        setPanY(p.panY);
      }
    };
    img.src = url;

    return () => {
      revoke();
      imageDimensionsRef.current = { width: 0, height: 0 };
      setPanX(0); setPanY(0); setScale(1); setRotation(0);
    };
  }, [selectedCategory, orthoData]);

  //non-passive wheel listener
  React.useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault();

      if (!imageDimensionsRef.current.width) return;

      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;

      const zoomFactor = Math.exp(-e.deltaY * 0.001);
      const newRel = Math.min(5, Math.max(0.5, relativeZoom * zoomFactor));
      const newScale = fitScaleRef.current * newRel;
      const k = newScale / scale;
      const nx = cx - k * (cx - panX);
      const ny = cy - k * (cy - panY);

      setPanX(nx);
      setPanY(ny);
      setRelativeZoom(newRel);
      setScale(newScale);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [relativeZoom, scale, panX, panY]);

  //User experience improvement - end panning if themouse leaves the box fast
  React.useEffect(() => {
    const up = () => setIsPanning(false);
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const saveTransformedSnapshot = () => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    if (!image || !canvas || !imageUrl || !imageDimensionsRef.current.width || !imageDimensionsRef.current.height) return;

    const VIEWER_SIZE = 650;
    const DPR = window.devicePixelRatio || 1;
    const QUALITY_MULT = 2;
    const OUTPUT_SIZE = Math.min(Math.round(VIEWER_SIZE * DPR * QUALITY_MULT), 4096)

    if (saving) return;
    setSaving(true);

    const natW = imageDimensionsRef.current.width;
    const natH = imageDimensionsRef.current.height;

    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.restore();

    ctx.imageSmoothingEnabled = scale < 1;
    ctx.imageSmoothingQuality = 'high';

    const pxScale = OUTPUT_SIZE / VIEWER_SIZE;
    ctx.save();
    ctx.scale(pxScale, pxScale);

    ctx.beginPath();
    ctx.rect(0, 0, VIEWER_SIZE, VIEWER_SIZE);
    ctx.clip();

    ctx.translate(panX, panY);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(scale, scale);

    ctx.drawImage(image, 0, 0, natW, natH);

    ctx.restore();

    const trimTransparent = (srcCanvas, alphaThreshold = 1, padding = 0) => {
      const w = srcCanvas.width, h = srcCanvas.height;
      const srcCtx = srcCanvas.getContext('2d');
      const pixels = srcCtx.getImageData(0, 0, w, h).data;

      let minX = w, minY = h, maxX = -1, maxY = -1;
      for (let y = 0; y < h; y++) {
        const row = y * w * 4;
        for (let x = 0; x < w; x++) {
          const a = pixels[row + x * 4 + 3];
          if (a >= alphaThreshold) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (maxX < minX || maxY < minY) return srcCanvas;

      minX = Math.max(0, minX - padding);
      minY = Math.max(0, minY - padding);
      maxX = Math.min(w - 1, maxX + padding);
      maxY = Math.min(h - 1, maxY + padding);

      const cw = Math.max(1, maxX - minX + 1);
      const ch = Math.max(1, maxY - minY + 1);

      const out = document.createElement('canvas');
      out.width = cw; out.height = ch;
      out.getContext('2d').drawImage(srcCanvas, minX, minY, cw, ch, 0, 0, cw, ch);
      return out;
    };

    const trimmedCanvas = trimTransparent(canvas, 1, 0);
    const snapshotUrl = trimmedCanvas.toDataURL('image/png');

    updateSectionProps(instanceId, {
      snapshotUrl,
      isCropped: true,
      rotation,
      scale,
      panX,
      panY,
    });

    setSavedPulse(true);
    setTimeout(() => {
      setSavedPulse(false);
      setSaving(false);
      onClose?.();
    }, 550);
  };

  const handleZoomChange = (relativeValue) => {
    const newRel = Math.min(5, Math.max(0.5, relativeValue));
    const newScale = fitScaleRef.current * newRel;
    const cx = VIEWER_SIZE / 2;
    const cy = VIEWER_SIZE / 2;

    const k = newScale / scale;
    const nx = cx - k * (cx - panX);
    const ny = cy - k * (cy - panY);

    setPanX(nx);
    setPanY(ny);
    setRelativeZoom(newRel);
    setScale(newScale);
  };

  return (
    <div className="edit-panel">
      <div className="ortho-grid">

        {/* Category Selecta */}
        <div className="ortho-field ortho-field--span2">
          <label className="ortho-label">Select Orthomosaic Image</label>
          <select
            value={selectedCategory || ''}
            onChange={(e) => {
              updateSectionProps(instanceId, { category: e.target.value, snapshotUrl: null });
              setRotation(0);
              setScale(1);
            }}
          >
            <option value="">-- Select --</option>
            {availableCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* rotate (left col) */}
        <div className="ortho-field">
          <label className="ortho-label">
            Rotate <span className="ortho-hint">({rotation}°)</span>
          </label>
          <input
            type="range"
            min="0"
            max="360"
            step="1"
            value={rotation}
            onChange={(e) => setRotation(Number(e.target.value))}
          />
        </div>

        {/* zoom (right col) */}
        <div className="ortho-field">
          <label className="ortho-label">
            Zoom <span className="ortho-hint">({relativeZoom.toFixed(2)}x)</span>
          </label>
          <input
            type="range"
            min={0.5}
            max={5}
            step="0.01"
            value={relativeZoom}
            onChange={(e) => handleZoomChange(Number(e.target.value))}
          />
        </div>

        <div className="ortho-divider" />

        {/* image size (left col) */}
        <div className="ortho-field">
          <label className="ortho-label">
            Image Size <span className="ortho-hint">({Math.round((p.exportScale ?? 0.9) * 100)}%)</span>
          </label>
          <div className="ortho-control-row">
            <input
              type="range"
              min={30}
              max={100}
              step={1}
              value={Math.round((p.exportScale ?? 0.9) * 100)}
              onChange={(e) => {
                const pct = Number(e.target.value);
                updateSectionProps(instanceId, { exportScale: pct / 100 });
                pingGhost();
              }}
            />
          </div>
        </div>

        {/* alignment (right col) */}
        <div className="ortho-field ortho-field--align">
          <label className="ortho-label">Align</label>
          <div className="ortho-control-row" style={{ gap: 8 }}>
            {['left', 'center', 'right'].map(pos => (
              <button
                key={pos}
                type="button"
                onClick={() => {
                  updateSectionProps(instanceId, { exportAlign: pos });
                  pingGhost();
                }}
                className={`seg-btn ${(p.exportAlign ?? 'center') === pos ? 'on' : ''}`}
              >
                {pos}
              </button>
            ))}
          </div>
        </div>
      </div>

      {selectedCategory && (
        <>
          <div
            ref={viewerRef}
            className={`image-display ${isPanning ? 'grabbing' : ''}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onContextMenu={(e) => e.preventDefault()}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >

            {imageUrl ? (
              <img
                ref={imageRef}
                src={imageUrl}
                alt={`Selected Ortho: ${selectedCategory}`}
                onDragStart={handleImageDragStart}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: `translate(${panX}px, ${panY}px) rotate(${rotation}deg) scale(${scale})`,

                  transformOrigin: '0 0',
                  width: imageDimensionsRef.current.width,
                  height: imageDimensionsRef.current.height,
                }}
              />
            ) : (
              <div className="muted" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                Loading image data from server...
              </div>
            )}

            {/* LIVE EXPORT SIZE GHOST */}
            {(() => {
              const exportScale = p.exportScale ?? 0.9;
              const ghostSide = VIEWER_SIZE * exportScale;
              const align = (p.exportAlign ?? 'center');

              const top = (VIEWER_SIZE - ghostSide) / 2;
              const left =
                align === 'left'
                  ? 0
                  : align === 'right'
                    ? (VIEWER_SIZE - ghostSide)
                    : (VIEWER_SIZE - ghostSide) / 2;

              return (
                <div
                  className={`size-ghost ${showSizeGhost ? 'on' : ''}`}
                  style={{
                    width: ghostSide,
                    height: ghostSide,
                    top,
                    left,
                  }}
                  aria-hidden="true"
                />
              );
            })()}

            <div className="viewer-toolbar" aria-label="Crop actions">
              <button
                type="button"
                className="tool-btn"
                onClick={resetView}
                disabled={!imageUrl}
                title="Reset view"
              >
                <RestartAltIcon fontSize="medium" />
                <span>Reset</span>
              </button>
              <button
                type="button"
                className="tool-btn primary"
                onClick={saveTransformedSnapshot}
                disabled={!imageUrl}
                title="Save snapshot"
              >
                <SaveIcon fontSize="medium" />
                <span>{saving ? 'Saving…' : 'Save'}</span>
              </button>
            </div>

            {savedPulse && (
              <div
                style={{
                  position: 'absolute',
                  top: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0,0,0,0.6)',
                  color: '#e7ffe7',
                  padding: '6px 12px',
                  borderRadius: 8,
                  fontSize: 12,
                  zIndex: 30,
                  pointerEvents: 'none',
                }}
              >
                Saved
              </div>
            )}
          </div>

          <canvas ref={canvasRef} style={{ display: 'none' }} />

          <div style={{ clear: 'both' }} />
        </>
      )}
      {!orthoData && <div className="muted">Loading site overlay data...</div>}
    </div>
  );
}

function OrthoPreview({ instanceId, active }) {
  const p = active?.sectionPropsById?.[instanceId] || {};
  const { category, snapshotUrl } = p;

  if (!snapshotUrl) {
    return <div className="placeholder" style={{ minHeight: 250 }}>
      Edit tile to select orthomosaic site image
    </div>;
  }

  const scale = typeof p.exportScale === 'number' ? p.exportScale : 0.9;
  const align = p.exportAlign ?? 'center';

  const wrapStyle =
    align === 'left'
      ? { width: `${Math.round(scale * 100)}%`, margin: '12px 0', marginRight: 'auto' }
      : align === 'right'
        ? { width: `${Math.round(scale * 100)}%`, margin: '12px 0', marginLeft: 'auto' }
        : { width: `${Math.round(scale * 100)}%`, margin: '12px auto' };


  return (
    <div style={wrapStyle}>
      <img
        src={snapshotUrl}
        alt={`Exported Ortho Image: ${category}`}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          border: '1px solid #ccc'
        }}
      />
      <h4 style={{ margin: '0 0 10px 0', textAlign: 'center' }}>{category}</h4>
    </div>
  );
}

/* --- Blocks --- */

function FaultTablePreview({ faults, props, exportSlice }) {

  const normBlock = (s) => {
    const m = String(s || '').match(/\d+/);
    return m ? m[0] : '';
  }

  const allUniqueLevels = React.useMemo(() => {
    //finds all level types, ususlaly 1,2,3 but in case there are unique ones to the site
    if (!Array.isArray(faults)) return [];
    return [...new Set(faults.map(f => f.level).filter(Boolean))];
  }, [faults]);

  const allUniqueCategories = React.useMemo(() => {
    if (!Array.isArray(faults)) return [];
    return [...new Set(faults.map(f => f.category).filter(Boolean))];
  }, [faults]);

  const allUniqueBlocks = React.useMemo(() => {
    if (!Array.isArray(faults)) return [];
    return [...new Set(faults.map(f => extractBlockId(f.location)).filter(Boolean))];
  }, [faults]);

  if (!Array.isArray(faults) || faults.length === 0) {
    return <div className="placeholder">No fault data available.</div>;
  }

  const p = props || {};

  //Filtering Logic
  const levelOn = p.levels || allUniqueLevels.reduce((acc, lvl) => ({ ...acc, [lvl]: true }), {});
  const categoryOn = p.categories || allUniqueCategories.reduce((acc, cat) => ({ ...acc, [cat]: true }), {});
  const blockOnRaw = p.blocks;
  const blockOn =
    blockOnRaw && Object.keys(blockOnRaw).length
      ? Object.fromEntries(
        Object.entries(blockOnRaw).map(([k, v]) => [normBlock(k), v])
      )
      : allUniqueBlocks.reduce((acc, block) => {
        acc[normBlock(block)] = true;
        return acc;
      }, {});

  const filteredRows = faults.filter(f => {
    const lvl = String(f.level || '').trim();
    const cat = String(f.category || '').trim();

    const formattedBlockName = extractBlockId(f.location);
    const blk = normBlock(formattedBlockName);

    const isLevelSelected = levelOn[lvl] === true;
    const isCategorySelected = categoryOn[cat] === true;
    const isBlockSelected = blockOn[blk] === true;

    return isLevelSelected && isCategorySelected && isBlockSelected;
  });

  //Sorting
  const sortedRows = filteredRows.sort((a, b) => {
    const levelOrder = { 'Level 3': 3, 'Level 2': 2, 'Level 1': 1 };
    const orderA = levelOrder[a.level] || 0;
    const orderB = levelOrder[b.level] || 0;

    if (orderA !== orderB) return orderB - orderA;
    const idA = a.ID || '';
    const idB = b.ID || '';
    return idA.localeCompare(idB);
  });

  //Limiting and Slicing
  const data = exportSlice
    ? sortedRows.slice(exportSlice.start, exportSlice.end)
    : sortedRows;

  if (data.length === 0) {
    return <div className="placeholder">No faults match the current filters.</div>;
  }

  //Rendering
  return (
    <table className="xp-table" data-splittable="rows">
      <thead>
        <tr>
          <th>ID</th>
          <th>Level</th>
          <th>Category</th>
          <th>Location</th>
          <th>Latitude</th>
          <th>Longitude</th>
        </tr>
      </thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={`${r.ID || r.location}-${i}`}>
            <td>{r.ID || '—'}</td>
            <td>{r.level || '—'}</td>
            <td>{r.category || '—'}</td>
            <td>{r.location || '—'}</td>
            <td>{r.latitude ? r.latitude.toFixed(7) : '-'}</td>
            <td>{r.longitude ? r.longitude.toFixed(7) : '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SectionBlock(props) {
  const {
    instance, kind, enabled, onMoveUp, onMoveDown, onRemove, active,
    updateSectionProps, isActive, onActivate,
    overview, faultChart, repairList,
    faults, orthoData, routeSite, siteName
  } = props;

  const allMapData = useMapData(active);

  const [editing, setEditing] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const hoverTimer = useRef(null);

  const isTitle = kind === 'TITLE';
  const isDate = kind === 'DATE';
  const isOverview = kind === 'OVERVIEW';
  const isMap = kind === 'MAP';
  const isFaultTable = kind === 'FAULT_CHART';
  const isOrtho = kind === 'ORTHO';
  const isCustomText = kind === 'CUSTOM_TEXT';
  const isRepairList = kind === 'REPAIR_LIST';

  //Fuck it im putting custom text stuff here why not

  const customTextOriginalRef = React.useRef(null);
  const [customTextDraft, setCustomTextDraft] = React.useState(null);

  React.useEffect(() => {
    if (!(isCustomText && isActive && editing)) return;

    const live = {
      ...DEFAULT_CUSTOM_TEXT_PROPS,
      ...(active?.sectionPropsById?.[instance.id] || {}),
    };
    if (!customTextOriginalRef.current) {
      customTextOriginalRef.current = live;
    }

    setCustomTextDraft(live);
  }, [isCustomText, isActive, editing, instance.id, active?.sectionPropsById]);

  //Checks for all kind of level types. usually level 1,2,3 but in case theres something unique
  const uniqueLevels = React.useMemo(() => {
    if (!Array.isArray(faults)) return [];
    const levels = [...new Set(faults.map(f => f.level).filter(Boolean))];
    return levels.sort();
  }, [faults]);

  //Checks for unique categories to render, made for the fault table but we will see if this stays
  const uniqueCategories = React.useMemo(() => {
    if (!Array.isArray(faults)) return [];
    const categories = [...new Set(faults.map(f => f.category).filter(Boolean))];
    return categories.sort();
  }, [faults]);

  //Checks for how many blocks there are based on unique number of block numbers
  const uniqueBlocks = React.useMemo(() => {
    if (!Array.isArray(faults)) return [];
    const blockNames = faults.map(f => extractBlockId(f.location)).filter(Boolean);

    return [...new Set(blockNames)].sort((a, b) => {
      const numA = parseInt(a.match(/\d+/)?.[0] || '0', 10);
      const numB = parseInt(b.match(/\d+/)?.[0] || '0', 10);
      return numA - numB;
    });
  }, [faults]);

  //These are for the fault table
  const [isLevelsOpen, setIsLevelsOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isBlocksOpen, setIsBlocksOpen] = useState(false);
  const handleToggleAllCheckbox = (filterType, allOptions) => {
    const currentFilters = active?.sectionPropsById?.[instance.id]?.[filterType] || {};

    // Check if ALL options are currently selected (default to true if map is empty)
    const isAllSelected = allOptions.every(option => currentFilters[option] !== false);

    // The goal is to set everything to the opposite of the current state
    const shouldSelect = !isAllSelected;

    // Create the new state map:
    const newFilters = allOptions.reduce((acc, option) => {
      acc[option] = shouldSelect;
      return acc;
    }, {});

    // Update the section props
    updateSectionProps(instance.id, {
      [filterType]: newFilters
    });
  };


  // Map props
  const mapData = isMap ? allMapData : {};
  const { blocks } = useBlockFeatures(mapData?.blockOutline);
  const availableBlocks = blocks.map(b => b.id);

  //UX for cursor to show and hide toolbar for tile
  const handleMouseEnter = () => {
    hoverTimer.current = setTimeout(() => setShowToolbar(true), 296);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    setShowToolbar(false);
  };

  const ovProps = { ...DEFAULT_OVERVIEW_PROPS, ...(active?.sectionPropsById?.[instance.id] || {}) };
  const [ovDraft, setOvDraft] = useState(ovProps);

  useEffect(() => {
    if (editing && isOverview) {
      const latest = { ...DEFAULT_OVERVIEW_PROPS, ...(active?.sectionPropsById?.[instance.id] || {}) };
      setOvDraft(latest);
    }
  }, [editing, isOverview, instance.id]);

  // TITLE props
  const titleProps = active?.sectionPropsById?.[instance.id] || {};
  const baseText = titleProps.text ?? active?.name ?? 'Report Title';
  const baseSize = Number(titleProps.size ?? 32);
  const baseAlign = titleProps.align ?? 'left';
  const [draft, setDraft] = useState({ text: baseText, size: baseSize, align: baseAlign });
  const originalRef = useRef(null);

  useEffect(() => {
    if (editing && isTitle) {
      setDraft({ text: baseText, size: baseSize, align: baseAlign });
      if (!originalRef.current) {
        originalRef.current = { text: baseText, size: baseSize, align: baseAlign };
      }
    }
  }, [editing, isTitle, baseText, baseSize, baseAlign]);

  const confirmEdit = () => {
    if (isTitle) updateSectionProps(instance.id, { ...draft });
    originalRef.current = null;
    setEditing(false);
  };

  // --- DATE props
  const dateProps = active?.sectionPropsById?.[instance.id] || {};
  const [dateDraft, setDateDraft] = useState({
    style: dateProps.style ?? 'numeric',   // 'numeric' | 'long'
    order: dateProps.order ?? 'MDY',       // 'DMY' | 'MDY' | 'YMD'
    size: Number(dateProps.size ?? 16),
    align: dateProps.align ?? 'left',
    margin: Number(dateProps.margin ?? 12),
    showPrefix: !!dateProps.showPrefix,
    prefixText: dateProps.prefixText ?? 'Analysis completed',
    sep: dateProps.sep ?? 'dash',
  });

  useEffect(() => {
    if (editing && isDate) {
      setDateDraft({
        style: dateProps.style ?? 'numeric',
        order: dateProps.order ?? 'MDY',
        size: Number(dateProps.size ?? 16),
        align: dateProps.align ?? 'left',
        margin: Number(dateProps.margin ?? 12),

        showPrefix: !!dateProps.showPrefix,
        prefixText: dateProps.prefixText ?? 'Analysis completed',
        sep: dateProps.sep ?? 'dash',
      });
    }
  }, [editing, isDate, instance.id]);

  if (!enabled) return null;

  return (
    <section
      className={`xp-section ${isActive ? 'active' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => { e.stopPropagation(); onActivate?.(); }}
      tabIndex={0}
      role="button"
    >
      {showToolbar && (
        <div className="sec-toolbar fade-in">
          <div className="sec-title">{labelFor(kind)}</div>
          <div className="sec-actions" onClick={(e) => e.stopPropagation()}>

            {(isTitle || isDate || isOverview || isMap || isFaultTable || isOrtho || isCustomText || isRepairList) && (
              <IconButton
                onClick={() => setEditing(v => !v)}
                title="Edit"
                aria-label="Edit section"
                className="report-icon-btn"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}

            <IconButton
              onClick={onMoveUp}
              title="Move up"
              className="report-icon-btn"
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={onMoveDown}
              title="Move down"
              className="report-icon-btn"
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={onRemove}
              title="Remove"
              className="report-icon-btn"
            >
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}

      {/*  edit panel for Title */}
      {isTitle && isActive && editing && (
        <div className="edit-panel" onClick={(e) => e.stopPropagation()}>
          <div className="row">
            <label>Text</label>
            <input
              type="text"
              value={draft.text}
              onChange={(e) => {
                const val = e.target.value;
                setDraft(d => ({ ...d, text: val }));
                updateSectionProps(instance.id, { text: val });
              }}
            />
          </div>
          <div className="row">
            <label>Size</label>
            <input
              type="range"
              min="20" max="64" step="2"
              value={draft.size}
              onChange={(e) => {
                const val = Number(e.target.value);
                setDraft(d => ({ ...d, size: val }));
                updateSectionProps(instance.id, { size: val });
              }}
            />
            <span className="mono">{draft.size}px</span>
          </div>
          <div className="row">
            <label>Align</label>
            <div className="seg">
              {['left', 'center', 'right'].map(a => (
                <button
                  key={a}
                  className={`seg-btn ${draft.align === a ? 'on' : ''}`}
                  onClick={() => {
                    setDraft(d => ({ ...d, align: a }));
                    updateSectionProps(instance.id, { align: a });
                  }}
                  type="button"
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
          <div className="edit-actions">
            <button className="btn save" onClick={confirmEdit}>
              <CheckIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}

      {/* --- DATE edit panel --- */}
      {isDate && isActive && editing && (
        <div className="edit-panel" onClick={(e) => e.stopPropagation()}>

          <div className="row">
            <label>Style</label>
            <div className="seg">
              {['numeric', 'long'].map(s => (
                <button
                  key={s}
                  className={`seg-btn ${dateDraft.style === s ? 'on' : ''}`}
                  onClick={() => {
                    setDateDraft(d => ({ ...d, style: s }));
                    updateSectionProps(instance.id, { style: s });
                  }}
                >{s}</button>
              ))}
            </div>
          </div>

          <div className="row">
            <label>Order</label>
            <div className="seg">
              {['DMY', 'MDY', 'YMD'].map(o => (
                <button
                  key={o}
                  className={`seg-btn ${dateDraft.order === o ? 'on' : ''}`}
                  onClick={() => {
                    setDateDraft(d => ({ ...d, order: o }));
                    updateSectionProps(instance.id, { order: o });
                  }}
                >{o}</button>
              ))}
            </div>
          </div>

          <div className="row">
            <label>Size</label>
            <input
              type="range"
              min="12" max="48" step="1"
              value={dateDraft.size}
              onChange={(e) => {
                const val = Number(e.target.value);
                setDateDraft(d => ({ ...d, size: val }));
                updateSectionProps(instance.id, { size: val });
              }}
            />
            <span className="mono">{dateDraft.size}px</span>
          </div>

          <div className="row">
            <label>Align</label>
            <div className="seg">
              {['left', 'center', 'right'].map(a => (
                <button
                  key={a}
                  className={`seg-btn ${dateDraft.align === a ? 'on' : ''}`}
                  onClick={() => {
                    setDateDraft(d => ({ ...d, align: a }));
                    updateSectionProps(instance.id, { align: a });
                  }}
                >{a}</button>
              ))}
            </div>
          </div>

          <div className="row">
            <label>Margin</label>
            <input
              type="range"
              min="0" max="48" step="1"
              value={dateDraft.margin}
              onChange={(e) => {
                const val = Number(e.target.value);
                setDateDraft(d => ({ ...d, margin: val }));
                updateSectionProps(instance.id, { margin: val });
              }}
            />
            <span className="mono">{dateDraft.margin}px</span>
          </div>

          {/* Prefix toggle */}
          <div className="row">
            <label>Prefix</label>
            <div className="seg">
              <button
                type="button"
                className={`seg-btn ${dateDraft.showPrefix ? 'on' : ''}`}
                onClick={() => {
                  const val = !dateDraft.showPrefix;
                  setDateDraft(d => ({ ...d, showPrefix: val }));
                  updateSectionProps(instance.id, { showPrefix: val });
                }}
              >
                {dateDraft.showPrefix ? 'Shown' : 'Hidden'}
              </button>
            </div>
          </div>

          {/* Prefix text (only when on) */}
          {dateDraft.showPrefix && (
            <div className="row">
              <label>Prefix text</label>
              <input
                type="text"
                value={dateDraft.prefixText}
                onChange={(e) => {
                  const val = e.target.value;
                  setDateDraft(d => ({ ...d, prefixText: val }));
                  updateSectionProps(instance.id, { prefixText: val });
                }}
                placeholder="Analysis completed"
                style={{ width: 220 }}
              />
            </div>
          )}

          {dateDraft.showPrefix && (
            <div className="row">
              <label>Separator</label>
              <div className="seg">
                {[
                  { key: 'dash', label: '—' },
                  { key: 'colon', label: ':' },
                  { key: 'none', label: 'Space' },
                  { key: 'newline', label: 'New line' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`seg-btn ${dateDraft.sep === opt.key ? 'on' : ''}`}
                    onClick={() => {
                      setDateDraft(d => ({ ...d, sep: opt.key }));
                      updateSectionProps(instance.id, { sep: opt.key });
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="edit-actions">
            <button className="btn save" onClick={confirmEdit}>
              <CheckIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}

      {/* :):):) KPI edit panel :(:(:( */}
      {isOverview && isActive && editing && (
        <div className="edit-panel" onClick={(e) => e.stopPropagation()}>

          {/*Alignment adjuster */}
          <div className="row">
            {/*Margin slider */}
            <label>Bottom margin</label>
            <input
              type="range"
              min="0"
              max="48"
              step="1"
              value={Number(ovDraft.margin ?? 8)}
              onChange={(e) => {
                const val = Math.max(0, Number(e.target.value || 0));
                setOvDraft(d => ({ ...d, margin: val }));
                updateSectionProps(instance.id, { margin: val });
              }}
              style={{ width: 180 }}
            />
            <span className="mono" style={{ marginLeft: 8 }}>{ovDraft.margin}px</span>

            {/*Alignment adjuster */}
            <label>Align</label>
            <div className="seg">
              {['left', 'center', 'right'].map(a => (
                <button
                  key={a}
                  type="button"
                  className={`seg-btn ${ovDraft.align === a ? 'on' : ''}`}
                  onClick={() => { setOvDraft(d => ({ ...d, align: a })); updateSectionProps(instance.id, { align: a }); }}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          {/* Metric pickers */}
          <div className="row">
            <label>Metrics</label>
            <div className="stack">
              {[
                { key: 'showTotal', label: 'Total Panels', labelKey: 'labelTotal' },
                { key: 'showAffected', label: 'Affected', labelKey: 'labelAffected' },
                { key: 'showPercent', label: '% Affected', labelKey: 'labelPercent' },
                { key: 'showKwh', label: 'Est. kWh Lost', labelKey: 'labelKwh' },
              ].map(m => (
                <div key={m.key} className="metric-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={ovDraft[m.key]}
                      onChange={(e) => {
                        const val = e.target.checked;
                        setOvDraft(d => ({ ...d, [m.key]: val }));
                        updateSectionProps(instance.id, { [m.key]: val });
                      }}
                    /> {m.label}
                  </label>
                  <input
                    type="text"
                    value={ovDraft[m.labelKey]}
                    onChange={(e) => {
                      const val = e.target.value;
                      setOvDraft(d => ({ ...d, [m.labelKey]: val }));
                      updateSectionProps(instance.id, { [m.labelKey]: val });
                    }}
                    placeholder="Custom label"
                    style={{ marginLeft: 8, width: 180 }}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="edit-actions">
            <button className="btn save" onClick={confirmEdit}>
              <CheckIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}

      {/*Map Editing panel */}
      {isMap && isActive && editing && (
        <MapEditPanel
          props={{ ...(MAP_DEFAULT_PROPS), ...(active?.sectionPropsById?.[instance.id] || {}) }}
          onChange={(partial) => updateSectionProps(instance.id, partial)}
          onDone = {() => {
            setEditing(false);
          }}
          onBakeSnapshot={() => {
            const el = document.querySelector(`[data-measure-id="${instance.id}"] svg`)?.closest(".xp-section");
            if (!el) return;
            const html = el.innerHTML;
            updateSectionProps(instance.id, { snapshotSvg: html });
          }}
          availableBlocks={availableBlocks}
        />
      )}

      {/*Fault Table Editing panel */}
      {isFaultTable && isActive && editing && (
        <div
          className="edit-panel"
          onClick={(e) => e.stopPropagation()}
          style={{ minHeight: 210 }}
        >

          {/* Filter Container */}
          <div className="filter-dropdown-container" style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: '0 0 20%', maxWidth: '20%', position: 'relative' }}>
              <button
                className="seg-btn"
                onClick={() => setIsLevelsOpen(v => !v)}
                style={{
                  width: '100%',
                  justifyContent: 'space-between',
                  padding: '6px 10px',
                  color: "#fffbed",
                  backgroundColor: "#333"
                }}
              >
                <span>Level Severity </span>
                {isLevelsOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>

              {isLevelsOpen && (
                <div
                  className="stack fade-in"
                  style={{
                    maxHeight: 'calc(4 * 28px)',
                    overflowY: 'auto',
                    border: "1px solid #333",
                    borderRadius: "5px",
                    background: "#fffbed",
                    padding: 8,

                    position: 'absolute',
                    marginTop: -1,
                    left: 0,
                    width: '100%',
                    zIndex: 10,
                  }}
                >
                  <label
                    style={{
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      gap: 8,
                      fontWeight: 'bold',
                      marginBottom: 6,
                      paddingBottom: 6,
                      borderBottom: '1px solid #ddd'
                    }}
                    onClick={() => handleToggleAllCheckbox('levels', uniqueLevels)}
                  >
                    <span
                      style={{ width: 18, height: 18, border: '1px solid #333', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                      {/* Logic to determine if all are selected */}
                      {uniqueLevels.every(L => active?.sectionPropsById?.[instance.id]?.levels?.[L] !== false) &&
                        <CheckIcon style={{ fontSize: 14, color: '#285335' }} />
                      }
                    </span>
                    <span style={{ flexGrow: 1 }}>Select/Deselect All</span>
                  </label>

                  {uniqueLevels.map(L => (
                    <label key={L} style={{ height: 28, display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 8 }}>
                      <span
                        style={{ width: 18, height: 18, border: '1px solid #ccc', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        onClick={() => {
                          const initialLevels = uniqueLevels.reduce((acc, lvl) => ({ ...acc, [lvl]: true }), {});
                          const currentLevels = active?.sectionPropsById?.[instance.id]?.levels || initialLevels;
                          updateSectionProps(instance.id, {
                            levels: {
                              ...currentLevels,
                              [L]: !(currentLevels[L] !== false && (!active?.sectionPropsById?.[instance.id]?.levels || uniqueLevels.includes(L)))
                            }
                          });
                        }}
                      >
                        {active?.sectionPropsById?.[instance.id]?.levels?.[L] !== false && (!active?.sectionPropsById?.[instance.id]?.levels || uniqueLevels.includes(L)) &&
                          <CheckIcon style={{ fontSize: 14, color: '#285335' }} />
                        }
                      </span>
                      <span style={{ flexGrow: 1, whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{L}</span>

                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={{ flex: '0 0 35%', maxWidth: '35%', position: 'relative' }}>
              <button
                className="seg-btn"
                onClick={() => setIsCategoriesOpen(v => !v)}
                style={{
                  width: '100%',
                  justifyContent: 'space-between',
                  padding: '6px 10px',
                  color: "#fffbed",
                  backgroundColor: "#333"
                }}
              >
                <span>Categories </span>
                {isCategoriesOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>

              {isCategoriesOpen && (
                <div
                  className="stack fade-in"
                  style={{
                    maxHeight: 'calc(4 * 28px)',
                    overflowY: 'auto',
                    border: "1px solid #333",
                    borderRadius: "5px",
                    background: "#fffbed",
                    padding: 8,

                    position: 'absolute',
                    left: 0,
                    width: '100%',
                    marginTop: -1,
                    zIndex: 10,
                  }}
                >
                  <label
                    style={{
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      gap: 8,
                      fontWeight: 'bold',
                      marginBottom: 6,
                      paddingBottom: 6,
                      borderBottom: '1px solid #ddd'
                    }}
                    onClick={() => handleToggleAllCheckbox('categories', uniqueCategories)}
                  >
                    <span
                      style={{ width: 18, height: 18, border: '1px solid #333', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                      {uniqueCategories.every(L => active?.sectionPropsById?.[instance.id]?.levels?.[L] !== false) &&
                        <CheckIcon style={{ fontSize: 14, color: '#285335' }} />
                      }
                    </span>
                    <span style={{ flexGrow: 1 }}>Select/Deselect All</span>
                  </label>

                  {uniqueCategories.map(C => (
                    <label key={C} style={{ height: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 8 }}>
                      <span
                        style={{ width: 18, height: 18, border: '1px solid #ccc', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        onClick={() => {
                          const initialCategories = uniqueCategories.reduce((acc, cat) => ({ ...acc, [cat]: true }), {});
                          const currentCategories = active?.sectionPropsById?.[instance.id]?.categories || initialCategories;
                          updateSectionProps(instance.id, {
                            categories: {
                              ...currentCategories,
                              [C]: !(currentCategories[C] !== false && (!active?.sectionPropsById?.[instance.id]?.categories || uniqueCategories.includes(C)))
                            }
                          });
                        }}
                      >
                        {active?.sectionPropsById?.[instance.id]?.categories?.[C] !== false && (!active?.sectionPropsById?.[instance.id]?.categories || uniqueCategories.includes(C)) &&
                          <CheckIcon style={{ fontSize: 14, color: '#285335' }} />
                        }
                      </span>
                      <span style={{ flexGrow: 1, whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{C}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={{ flex: '0 0 30%', maxWidth: '30%', position: 'relative' }}>
              <button
                className="seg-btn"
                onClick={() => setIsBlocksOpen(v => !v)}
                style={{
                  width: '100%',
                  justifyContent: 'space-between',
                  padding: '6px 10px',
                  color: "#fffbed",
                  backgroundColor: "#333"
                }}
              >
                <span>Blocks</span>
                {isBlocksOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>

              {isBlocksOpen && (
                <div
                  className="stack fade-in"
                  style={{
                    maxHeight: 'calc(4 * 28px)',
                    overflowY: 'auto',
                    border: "1px solid #333",
                    borderRadius: "5px",
                    background: "#fffbed",
                    padding: 8,

                    position: 'absolute',
                    marginTop: -1,
                    left: 0,
                    width: '100%',
                    zIndex: 10,
                  }}
                >

                  <label
                    style={{
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      gap: 8,
                      fontWeight: 'bold',
                      marginBottom: 6,
                      paddingBottom: 6,
                      borderBottom: '1px solid #ddd'
                    }}
                    onClick={() => handleToggleAllCheckbox('blocks', uniqueBlocks)}
                  >
                    <span
                      style={{ width: 18, height: 18, border: '1px solid #333', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                      {uniqueBlocks.every(L => active?.sectionPropsById?.[instance.id]?.levels?.[L] !== false) &&
                        <CheckIcon style={{ fontSize: 14, color: '#285335' }} />
                      }
                    </span>
                    <span style={{ flexGrow: 1 }}>Select/Deselect All</span>
                  </label>

                  {uniqueBlocks.map(B => (
                    <label key={B} style={{ height: 28, display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 8 }}>
                      <span
                        style={{ width: 18, height: 18, border: '1px solid #ccc', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        onClick={() => {
                          const initialBlocks = uniqueBlocks.reduce((acc, block) => ({ ...acc, [block]: true }), {});
                          const currentBlocks = active?.sectionPropsById?.[instance.id]?.blocks || initialBlocks;
                          updateSectionProps(instance.id, {
                            blocks: {
                              ...currentBlocks,
                              [B]: !(currentBlocks[B] !== false && (!active?.sectionPropsById?.[instance.id]?.blocks || uniqueBlocks.includes(B)))
                            }
                          });
                        }}
                      >
                        {active?.sectionPropsById?.[instance.id]?.blocks?.[B] !== false && (!active?.sectionPropsById?.[instance.id]?.blocks || uniqueBlocks.includes(B)) &&
                          <CheckIcon style={{ fontSize: 14, color: '#285335' }} />
                        }
                      </span>
                      <span style={{ flexGrow: 1, whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{B}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

          </div>

          <div className="edit-actions">
            <button className="btn save" onClick={() => setEditing(false)}>
              <CheckIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}

      {/* Ortho Exporter editing panel */}
      {isOrtho && isActive && editing && (
        <OrthoEditor
          instanceId={instance.id}
          active={active}
          orthoData={orthoData}
          updateSectionProps={updateSectionProps}
          onClose={() => setEditing(false)}
        />
      )}

      {/* Custom Text Edit panel */}
      {isCustomText && isActive && editing && customTextDraft && (
        <div className="edit-panel" onClick={(e) => e.stopPropagation()}>

          {/* TYPOGRAPHY + SPACING */}
          <div className="row" style={{ alignItems: 'center', gap: 12 }}>
            <label>Font size</label>
            <input
              type="range" min="12" max="48" step="1"
              value={Number(customTextDraft.size)}
              onChange={(e) =>
                setCustomTextDraft((d) => ({ ...d, size: Number(e.target.value) }))
              }
              style={{ width: 180 }}
            />
            <span className="mono">{customTextDraft.size}px</span>

            <label>Weight</label>
            <input
              type="range" min="100" max="900" step="100"
              value={Number(customTextDraft.weight)}
              onChange={(e) =>
                setCustomTextDraft((d) => ({ ...d, weight: Number(e.target.value) }))
              }
              style={{ width: 160 }}
            />
            <span className="mono">{customTextDraft.weight}</span>

            <label>Align</label>
            <div className="seg">
              {['left', 'center', 'right'].map(a => (
                <button
                  key={a}
                  type="button"
                  className={`seg-btn ${customTextDraft.align === a ? 'on' : ''}`}
                  onClick={() =>
                    setCustomTextDraft((d) => ({ ...d, align: a }))
                  }
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div className="row" style={{ alignItems: 'center', gap: 12 }}>
            <label>Line height</label>
            <input
              type="range" min="1.0" max="2.0" step="0.05"
              value={Number(customTextDraft.lineHeight)}
              onChange={(e) =>
                setCustomTextDraft((d) => ({ ...d, lineHeight: Number(e.target.value) }))
              }
              style={{ width: 220 }}
            />
            <span className="mono">{Number(customTextDraft.lineHeight).toFixed(2)}</span>

            <label>Top</label>
            <input
              type="range" min="0" max="48" step="1"
              value={Number(customTextDraft.marginTop)}
              onChange={(e) =>
                setCustomTextDraft((d) => ({ ...d, marginTop: Number(e.target.value) }))
              }
              style={{ width: 120 }}
            />
            <span className="mono">{customTextDraft.marginTop}px</span>

            <label>Bottom</label>
            <input
              type="range" min="0" max="48" step="1"
              value={Number(customTextDraft.marginBottom)}
              onChange={(e) =>
                setCustomTextDraft((d) => ({ ...d, marginBottom: Number(e.target.value) }))
              }
              style={{ width: 120 }}
            />
            <span className="mono">{customTextDraft.marginBottom}px</span>
          </div>

          {/* TEXTAREA */}
          <div className="row">
            <label>Text</label>
            <textarea
              rows={4}
              value={customTextDraft.text}
              onChange={(e) =>
                setCustomTextDraft((d) => ({ ...d, text: e.target.value }))
              }
              onInput={(e) => {
                e.currentTarget.style.height = 'auto';
                e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
              }}
              style={{ width: '100%', resize: 'none' }}
              placeholder="Type your notes…"
            />
          </div>

          {/* ACTIONS */}
          <div className="edit-actions" style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn save"
              onClick={() => {
                // Commit draft to section props
                updateSectionProps(instance.id, { ...customTextDraft });
                // clear originals and close
                customTextOriginalRef.current = null;
                setCustomTextDraft(null);
                setEditing(false);
              }}
              title="Save"
            >
              Save
            </button>

            <button
              className="btn"
              onClick={() => {
                // Revert section props back to original (no changes)
                const orig = customTextOriginalRef.current;
                if (orig) updateSectionProps(instance.id, { ...orig });
                customTextOriginalRef.current = null;
                setCustomTextDraft(null);
                setEditing(false);
              }}
              title="Close without saving"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Repair List Edit panel :D */}
      {isRepairList && isActive && editing && (
        <RepairListEditPanel
          props={{ ...(REPAIR_DEFAULT_PROPS), ...(active?.sectionPropsById?.[instance.id] || {}) }}
          onChange={(partial) => updateSectionProps(instance.id, partial)}
          siteName={routeSite}
          onDone={() => setEditing(false)}
        />
      )}

      <SectionPreview
        kind={kind}
        instanceId={instance.id}
        enabled={enabled}
        active={active}
        overview={overview}
        faultChart={faultChart}
        repairList={repairList}
        updateSectionProps={updateSectionProps}
        faults={faults}
        mapInputs={mapData}
        inExport={false}
        siteName={routeSite}
      />
    </section >
  );
}

function labelFor(key) {
  return SECTION_CATALOG.find(s => s.key === key)?.label || key;
}

function SectionPreview({
  kind, instanceId, enabled, active,
  overview, faultChart, repairList,
  onNotesChange, exportSlice, updateSectionProps,
  faults, mapInputs, inExport = false, siteName
}) {
  if (!enabled) return null;

  switch (kind) {
    case 'TITLE': {
      const p = active?.sectionPropsById?.[instanceId] || {};
      const text = p.text ?? active?.name ?? 'Report Title';
      const size = Number(p.size ?? 32);
      const align = p.align ?? 'left';
      return (
        <h1
          style={{
            marginTop: 0,
            marginBottom: `${Math.round(size * 0.01)}px`,
            fontSize: size,
            textAlign: align,
            lineHeight: 1.1,
          }}
        >
          {text}
        </h1>
      );
    }

    case 'DATE': {
      const p = active?.sectionPropsById?.[instanceId] || {};
      const raw = p.value ?? active?.selectedDate ?? new Date();
      const dObj = parseDateFromString(raw) || new Date();
      const pretty = formatDateForDisplay(dObj, {
        style: p.style ?? 'numeric', // 'numeric' | 'long'
        order: p.order ?? 'MDY',     // 'DMY' | 'MDY' | 'YMD'
      });
      const size = Number(p.size ?? 16);
      const align = p.align ?? 'left';
      const margin = Number(p.margin ?? 6);

      const showPrefix = p.showPrefix !== false;
      const prefixText = p.prefixText ?? 'Analysis completed';
      const sep = p.sep ?? 'dash'; // 'dash' | 'colon' | 'none' | 'newline'

      const renderSep = () => {
        switch (sep) {
          case 'dash': return <span>&nbsp;—&nbsp;</span>;
          case 'colon': return <span>:&nbsp;</span>;
          case 'none': return <span>&nbsp;</span>;
          case 'newline': return <br />;
          default: return <span>&nbsp;—&nbsp;</span>;
        }
      };

      return (
        <div
          className="muted"
          style={{
            marginTop: 0,
            marginBottom: `${margin}px`,
            fontSize: size,
            textAlign: align,
            paddingLeft: align === 'left' ? '4px' : 0,
            paddingRight: align === 'right' ? '4px' : 0,
            lineHeight: 1.3,
            fontWeight: '700',
          }}
        >
          {showPrefix && (
            <>
              <span style={{ opacity: 0.9, fontWeight: '100', }}>{prefixText}</span>
              {renderSep()}
            </>
          )}
          {pretty}
        </div>
      );
    }

    case 'OVERVIEW': {
      const p = { ...DEFAULT_OVERVIEW_PROPS, ...(active?.sectionPropsById?.[instanceId] || {}) };

      const tot = overview?.totalPanels ?? 0;
      const aff = overview?.affectedPanels ?? 0;
      const pct = typeof overview?.percentAffected === 'number'
        ? overview.percentAffected
        : 0;
      const kwh = overview?.kwhLost;

      // layout classes (grid or row) + size
      const sizeClass = p.size === 'lg' ? 'kpi-lg' : p.size === 'sm' ? 'kpi-sm' : 'kpi-md';
      const wrapClass = p.layout === 'row' ? `kpi-wrap kpi-row ${sizeClass}` : `kpi-grid ${sizeClass}`;

      const items = [];
      if (p.showTotal) items.push({ label: p.labelTotal, value: fmt(tot, 0) });
      if (p.showAffected) items.push({ label: p.labelAffected, value: fmt(aff, 0) });
      if (p.showPercent) items.push({ label: p.labelPercent, value: `${fmt(pct, p.percentDecimals)}%` });
      if (p.showKwh) items.push({ label: p.labelKwh, value: (kwh == null ? '—' : fmt(kwh, p.kwhDecimals)) });

      return (
        <div
          className={wrapClass}
          style={{
            textAlign: p.align,
            marginTop: 0,
            marginBottom: `${p.margin}px`,
          }}
        >
          {items.map((it, idx) => (
            <div key={idx} className="kpi-card">
              <div className="kpi-label">{it.label}</div>
              <div className="kpi-val">{it.value}</div>
            </div>
          ))}
        </div>
      );
    }

    case 'FAULT_CHART': {
      const p = active?.sectionPropsById?.[instanceId] || {};
      return (
        <FaultTablePreview
          faults={faults}
          props={p}
          exportSlice={exportSlice}
        />
      );
    }

    case 'MAP':
      return (
        <MapSection
          instanceId={instanceId}
          active={active}
          siteMeta={null}
          mapInputs={mapInputs}
          faults={faults}
          updateSectionProps={updateSectionProps}
          editing={false}
          exportMode={!!exportSlice || inExport}
        />
      );

    case 'REPAIR_LIST': {
      return (
        <RepairListSection
          instanceId={instanceId}
          props={active?.sectionPropsById?.[instanceId] || {}}
          siteName={siteName}
          exportMode={!!exportSlice || inExport}
        />
      );
    }

    case 'CUSTOM_TEXT': {
      const p = { ...DEFAULT_CUSTOM_TEXT_PROPS, ...(active?.sectionPropsById?.[instanceId] || {}) };
      const commonStyle = {
        fontSize: p.size,
        fontWeight: p.weight,
        textAlign: p.align,
        lineHeight: p.lineHeight,
        fontFamily: p.fontFamily,
        marginTop: p.marginTop,
        marginBottom: p.marginBottom,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      };

      if (inExport || !onNotesChange) {
        return (
          <div className="xp-custom-text-block" style={{ ...commonStyle, padding: '4px' }}>
            {p.text || <span className="muted">— No custom notes —</span>}
          </div>
        );
      }

      return (
        <textarea
          rows={4}
          style={{ ...commonStyle, width: '100%', display: 'block', resize: 'none', border: '1px solid #ddd', padding: 6 }}
          placeholder="Add your custom notes here…"
          defaultValue={p.text}
          onInput={(e) => {
            e.currentTarget.style.height = 'auto';
            e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
          }}
          onChange={(e) => updateSectionProps?.(instanceId, { text: e.target.value })}
        />
      );
    }

    case 'ORTHO':
      return (
        <OrthoPreview
          instanceId={instanceId}
          active={active}
        />
      );

    default:
      return null;
  }
}

function CoverPreview({
  active,
  routeSite,
  resolvedDate,
  updateCoverProps,
  mode = "preview",
  closeToolsTick,
}) {
  const DEFAULT_LOGO = "/HZVTLogo.png";
  const cover = active?.sectionProps?.COVER || {};
  const elements = cover.elements || [];

  useEffect(() => {
    const needsSeed =
      !cover.elements || !Array.isArray(cover.elements) || cover.elements.length === 0;

    if (!needsSeed) return;

    const title =
      cover.title ?? active?.sectionProps?.TITLE?.text ?? active?.name ?? "Report Title";
    const subtitle = cover.subtitle ?? "Prepared report";
    const logoSrc = cover.logoRemoved ? null : cover.logoSrc || DEFAULT_LOGO;

    const seeded = [];
    if (logoSrc) {
      seeded.push({
        id: uid(),
        type: "image",
        src: logoSrc,
        width: 180,
        margin: 12,
      });
    }
    seeded.push({
      id: uid(),
      type: "text",
      text: title,
      size: 44,
      weight: 800,
      spacing: 0,
      margin: 12,
    });
    seeded.push({
      id: uid(),
      type: "text",
      text: subtitle,
      size: 16,
      weight: 400,
      spacing: 0,
      margin: 6,
    });
    seeded.push({
      id: uid(),
      type: "meta",
      site: routeSite || "—",
      date: resolvedDate || new Date().toLocaleDateString(),
      size: 13,
      margin: 10,
    });

    updateCoverProps({
      elements: seeded,
      logoRemoved: undefined,
      logoSrc: undefined,
    });
  }, [active?.id]);

  //Mutators for elements 
  const patch = (next) => updateCoverProps({ elements: next });
  const setOne = (id, updates) =>
    patch(elements.map((el) => (el.id === id ? { ...el, ...updates } : el)));
  const removeOne = (id) => patch(elements.filter((el) => el.id !== id));

  const [selectedId, setSelectedId] = React.useState(null);
  const selectedEl = elements.find((e) => e.id === selectedId) || null;

  const fileInputRef = useRef(null);
  const handlePickImage = () => fileInputRef.current?.click();

  const addText = () =>
    patch([
      ...elements,
      {
        id: uid(),
        type: "text",
        text: "New text",
        size: 24,
        weight: 600,
        spacing: 0,
        margin: 8,
      },
    ]);

  const addImageFromFile = (file) => {
    if (file?.type?.startsWith("image/")) {
      patch([
        ...elements,
        {
          id: uid(),
          type: "image",
          src: URL.createObjectURL(file),
          width: 200,
          margin: 12,
        },
      ]);
    }
  };
  const onPick = (e) => addImageFromFile(e.target.files?.[0]);

  // click-away to close
  const onCanvasClick = () => {
    if (mode === "preview") setSelectedId(null);
  };

  // ESC to close
  useEffect(() => {
    if (mode !== "preview") return;
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode]);

  useEffect(() => {
    setSelectedId(null);
  }, [closeToolsTick, mode]);

  return (
    <div className={`cover-page ${mode === "export" ? "is-export" : ""}`}>
      {mode === "preview" && selectedEl && (
        <div className="cv-toptools" onClick={(e) => e.stopPropagation()}>
          <div className="cv-toptools-title">
            {selectedEl.type === "image"
              ? "Image"
              : selectedEl.type === "meta"
                ? "Metadata"
                : "Text"}
          </div>

          {selectedEl.type === "text" && (
            <>
              <label className="cv-tool">
                size
                <input
                  type="range"
                  min="12"
                  max="72"
                  step="1"
                  value={selectedEl.size ?? 24}
                  onChange={(e) =>
                    setOne(selectedEl.id, { size: Number(e.target.value) })
                  }
                />
              </label>
              <label className="cv-tool">
                weight
                <input
                  type="range"
                  min="100"
                  max="900"
                  step="100"
                  value={selectedEl.weight ?? 600}
                  onChange={(e) =>
                    setOne(selectedEl.id, { weight: Number(e.target.value) })
                  }
                />
              </label>
            </>
          )}

          {selectedEl.type === "image" && (
            <label className="cv-tool">
              size
              <input
                type="range"
                min="80"
                max="420"
                step="4"
                value={selectedEl.width ?? 200}
                onChange={(e) =>
                  setOne(selectedEl.id, { width: Number(e.target.value) })
                }
              />
            </label>
          )}

          <label className="cv-tool">
            margin
            <input
              type="range"
              min="0"
              max="40"
              step="1"
              value={selectedEl.margin ?? 0}
              onChange={(e) =>
                setOne(selectedEl.id, { margin: Number(e.target.value) })
              }
            />
          </label>

          {selectedEl.type !== "meta" && (
            <button
              className="cv-del"
              onClick={() => {
                removeOne(selectedEl.id);
                setSelectedId(null);
              }}
            >
              Delete
            </button>
          )}
          <button className="cv-close" onClick={() => setSelectedId(null)}>
            x
          </button>
        </div>
      )}

      <div className="cover-canvas" onClick={onCanvasClick}>
        {elements.map((el) => {
          const isActive = el.id === selectedId && mode === "preview";
          const wrapProps = {
            key: el.id,
            className: `cv-el ${el.type} ${isActive ? "active" : ""}`,
            style: { marginTop: el.margin ?? 0 },
            onClick: (e) => {
              e.stopPropagation();
              if (mode === "preview") setSelectedId(el.id);
            },
          };

          if (el.type === "image") {
            return (
              <div {...wrapProps}>
                <img
                  className="cv-img-el"
                  src={el.src}
                  alt=""
                  style={{ width: el.width ?? 200 }}
                />
              </div>
            );
          }

          if (el.type === "meta") {
            return (
              <div
                {...wrapProps}
                style={{ ...wrapProps.style, fontSize: el.size ?? 13 }}
              >
                <div>
                  <span className="muted">Site:</span> {el.site}
                </div>
                <div>
                  <span className="muted">Date:</span> {el.date}
                </div>
              </div>
            );
          }

          // text
          return (
            <div {...wrapProps}>
              {mode === "export" ? (
                <div
                  className="cv-text-block"
                  style={{
                    fontSize: el.size ?? 24,
                    fontWeight: el.weight ?? 600,
                    textAlign: "center",
                  }}
                >
                  {el.text ?? ""}
                </div>
              ) : (
                <textarea
                  className="cv-text-block cv-text-input"
                  value={el.text ?? ""}
                  onChange={(e) => {
                    setOne(el.id, { text: e.target.value });
                    e.currentTarget.style.height = "auto";
                    e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                  }}
                  style={{
                    fontSize: el.size ?? 24,
                    fontWeight: el.weight ?? 600,
                    textAlign: "center",
                    height: "auto",
                    minHeight: "1.2em",
                  }}
                />
              )}
            </div>
          );
        })}

        {mode === "preview" && (
          <div className="cover-controls">
            <button onClick={addText}>+ Text</button>
            <button onClick={handlePickImage}>+ Image</button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="image/*"
              onChange={onPick}
            />
          </div>
        )}
      </div>

      <div className="cover-footer-fixed muted">
        Powered by HorizonView Technologies
      </div>
    </div>
  );
}

function TOCPreview({ active, routeSite, resolvedDate }) {
  const items = (active.order || []).map(inst => {
    const label = SECTION_CATALOG.find(s => s.key === inst.kind)?.label || inst.kind;
    return { key: inst.id, label };
  });

  return (
    <div className="toc-page">
      <h2 className="toc-title">Table of Contents</h2>
      {items.length === 0 ? (
        <div className="muted">No sections selected yet.</div>
      ) : (
        <ul className="toc-list">
          {items.map((it, idx) => (
            <li key={it.key}>
              <span className="toc-label">{it.label}</span>
              <span className="toc-dots" />
              <span className="toc-page-num">—</span>
            </li>
          ))}
        </ul>
      )}
      <div className="toc-meta muted">
        <div><b>Site:</b> {routeSite || '—'}</div>
        <div><b>Date:</b> {resolvedDate || new Date().toLocaleDateString()}</div>
      </div>
    </div>
  );
}

